import { Fragment, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { Title } from './widgets/Title';

import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';

export function getOptions(options, randomize) {
    let opts = options || [];
    // if (randomize) {
    //     opts = shuffleArray(opts);
    // }
    return opts;
}

export function getOptionFromConfig(option, config, multiple) {
    let opt = option;

    if (!config) {
        return opt;
    }

    if (config.value) {
        // This is to account for the quirky behaviour of onChange returning an array
        if (multiple && Array.isArray(option)) {
            const value = [];
            for (const item of option) {
                if (isObject(item)) {
                    value.push(get(item, config.value));
                } else {
                    value.push(item);
                }
            }
            opt = value;
        } else {
            opt = get(option, config.value);
        }
    }

    return opt;
}

/*
  Required to handle quirky behaviour of Autocomplete component
  where it returns the option object when opening the selection box
  and returns the option value upon selection
  */
export function getLabel(option, options, config, multiple) {
    // if not config, options must be primitive
    if (!config) {
        return String(option);
    }
    if (isObject(option)) {
        return String(get(option, config.label));
    }
    // if option is not an object, get option by value
    // then get the label based on config
    if (config?.value) {
        const o =
            options.find(
                o => getOptionFromConfig(o, config, multiple) === option,
            ) || {};
        return !String(get(o, config.label))
            ? ''
            : String(get(o, config.label));
    }

    // if all else fails, sreturn option as string
    return String(option || '');
}

export default function CustomAutocomplete(props) {
    const {
        field: fieldConfig,
        methods: {
            control,
            getValues,
            setValue,
            trigger,
            formState: { errors },
        },
        hideTitle,
    } = props;
    const [focused, setFocused] = useState();

    const options = getOptions(
        fieldConfig.options,
        fieldConfig.randomizeOptions,
    );

    function componentProps(fieldConfig, value) {
        const multiple = fieldConfig.props?.multiple || false;

        const disableClearable = fieldConfig.props?.disableClearable || false;
        const freeSolo = fieldConfig.props?.freeSolo || false;

        let props = {
            id: fieldConfig.id || fieldConfig.attribute,
            size: 'small',
            disableClearable: true,
            fullWidth: true,
            isOptionEqualToValue: (option, value) => {
                // Handle empty/null/undefined values
                if (!value || value === '') {
                    return false;
                }
                // Required to handle the quirky behaviour of Autocomplete component
                // where it returns the value object sometimes and value value sometimes
                return isObject(value)
                    ? isEqual(
                          getOptionFromConfig(
                              option,
                              fieldConfig.optionConfig,
                              multiple,
                          ),
                          getOptionFromConfig(
                              value,
                              fieldConfig.optionConfig,
                              multiple,
                          ),
                      )
                    : getOptionFromConfig(
                          option,
                          fieldConfig.optionConfig,
                          multiple,
                      ) === value;
            },
            getOptionLabel: option =>
                (fieldConfig?.getOptionLabel &&
                    fieldConfig.getOptionLabel(option)) ||
                getLabel(option, options, fieldConfig.optionConfig),
            onChange: (event, option) => {
                setValue(
                    fieldConfig.attribute,
                    getOptionFromConfig(
                        option,
                        fieldConfig.optionConfig,
                        multiple,
                    ),
                );
                // trigger(fieldConfig.attribute);
            },
            onBlur: () => {
                setFocused(false);
                // trigger(fieldConfig.attribute);
            },
            onFocus: () => setFocused(true),
            value: value || (multiple ? [] : null),
            options,

            renderInput: params => (
                <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    inputProps={{
                        ...params.inputProps,
                        autoComplete: 'off', // disable autocomplete and autofill
                    }}
                    label={fieldConfig.label}
                    error={!!get(errors, fieldConfig.attribute)}
                    helperText={get(errors, fieldConfig.attribute)?.message}
                    sx={{
                        padding: '20px 0',
                        '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment':
                            {
                                top: '2px',
                            },
                        '& .MuiFormLabel-root-MuiInputLabel-root': {
                            top: '2px',
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        style: {},
                    }}
                />
            ),
            ...fieldConfig.props,
        };

        if (multiple) {
            props = {
                ...props,
                renderTags: (value, getTagProps) => {
                    return value.map((option, index) => (
                        <Chip
                            variant="outlined"
                            size="small"
                            label={getLabel(
                                option,
                                options,
                                fieldConfig.optionConfig,
                            )}
                            {...getTagProps({ index })}
                            key={index}
                        />
                    ));
                    // }
                },
            };
        }

        return props;
    }

    return (
        <Controller
            name={fieldConfig.attribute}
            control={control}
            render={({ field }) => (
                <Fragment>
                    {!hideTitle && fieldConfig.title && (
                        <Title field={fieldConfig} />
                    )}
                    <Autocomplete
                        {...componentProps(fieldConfig, field.value)}
                    />
                </Fragment>
            )}
        />
    );
}
